import { ButtonTrashIcon, PlusIcon, SaveIcon, XIcon } from 'common/components/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Icon, Button } from 'semantic-ui-react';

// Shared StyledButton component with props for dynamic customization
/**
 * @param {{
 *   label?: string;
 *   color?: 'blue' | 'gray' | 'orange' | 'green' | 'red';
 *   icon?: import('react').ReactNode;
 *   width?: string;
 *   height?: string;
 *   disabled?: boolean;
 *   onClick: (...args: any[]) => void;
 *   [key: string]: any;
 * }} props
 */
const StyledButton = ({
  label = 'Button',
  color = 'gray',
  icon = null,
  width = 'auto',
  height = '30px',
  disabled = false,
  onClick,
  ...rest
}) => (
  <StyledButtonStyles
    as="button"
    type="button"
    role="button"
    variant={color}
    onClick={!disabled ? onClick : () => { }}
    width={width}
    height={height}
    disabled={disabled}
    {...rest}
  >
    {icon && <ButtonIcon>{icon}</ButtonIcon>}
    <ButtonText disabled={disabled}>{label}</ButtonText>
  </StyledButtonStyles>
);

StyledButton.propTypes = {
  label: PropTypes.string,
  color: PropTypes.oneOf(['blue', 'gray', 'orange', 'green', 'red']),
  icon: PropTypes.element,
  width: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};

export { StyledButton };

// Specialized Buttons using the generic StyledButton
const StyledSaveButton = ({
  label = 'Save',
  color = 'blue',
  width = '100px',
  onClick,
  disabled = false,
  ...rest
}) => (
  <StyledButton
    label={label}
    color={color}
    icon={<SaveIcon />}
    width={width}
    onClick={!disabled ? onClick : () => { }}
    disabled={disabled}
    {...rest}
  />
);

const StyledCancelButton = ({
  label = 'Cancel',
  color = 'gray',
  width = '100px',
  onClick,
  disabled = false,
  ...rest
}) => (
  <StyledButton
    label={label}
    color={color}
    icon={<XIcon />}
    width={width}
    onClick={onClick}
    disabled={disabled}
    {...rest}
  />
);

const StyledArchiveButton = ({
  label = 'Archive',
  color = 'orange',
  width = '100px',
  onClick,
  disabled = false,
}) => (
  <StyledButton
    label={label}
    color={color}
    icon={<Icon name="archive" style={{ color: 'white', marginTop: '-6px' }} />}
    width={width}
    onClick={onClick}
    disabled={disabled}
  />
);

const StyledDeleteButton = ({
  label = 'Delete',
  color = 'red',
  width = '100px',
  onClick,
  disabled = false,
}) => (
  <StyledButton
    label={label}
    color={color}
    icon={<ButtonTrashIcon />}
    width={width}
    onClick={onClick}
    disabled={disabled}
  />
);

const StyledReactivateButton = ({
  label = 'Re-Activate',
  color = 'red',
  width = '120px',
  onClick,
}) => <StyledButton label={label} color={color} width={width} onClick={onClick} />;

const StyledAddButton = ({
  label = 'Add',
  color = 'green',
  width = '120px',
  height = '30px',
  onClick,
  disabled = false,
}) => (
  <StyledButton
    label={label}
    color={color}
    icon={<PlusIcon />}
    width={width}
    height={height}
    onClick={onClick}
    disabled={disabled}
  />
);

const StyledTrashButton = ({ onClick, id, disabled = false, title = 'Delete' }) => {
  const handleClick = () => {
    if (!disabled) {
      onClick(id);
    }
  };

  return (
    <span title={disabled ? title : undefined} style={{ display: 'inline-block' }}>
      <StyledIconButton
        icon="trash alternate"
        title={title}
        disabled={disabled}
        onClick={handleClick}
      />
    </span>
  );
};

const StyledIconButton = ({
  icon,
  title,
  disabled = false,
  onClick,
  color = '',
}) => (
  <Button
    type="button"
    icon
    title={title}
    disabled={disabled}
    {...(color ? { color } : {})}
    onClick={(event) => {
      event.stopPropagation();
      onClick();
    }}
    style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}
  >
    <Icon name={icon} style={{ margin: 0 }} />
  </Button>
);


export {
  StyledSaveButton,
  StyledCancelButton,
  StyledArchiveButton,
  StyledDeleteButton,
  StyledReactivateButton,
  StyledAddButton,
  StyledTrashButton,
  StyledIconButton,
};

// Simplified Styled Components

const colorMap = {
  blue: '#476dfa',
  gray: '#bbbbbb',
  orange: '#f3aa3d',
  green: '#28a745',
  red: '#e5657a',
  default: '#bbbbbb',
};

const hoverColorMap = {
  blue: '#6b8aff',
  gray: '#dddddd',
  orange: '#f6bd65',
  green: '#4ed46b',
  red: '#f28a9b',
  default: '#dddddd',
};

const StyledButtonStyles = styled.div`
  padding: 3px 25px;
  height: ${props => props.height || '30px'};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: ${props => props.width || 'auto'};
  white-space: nowrap;
  background: ${props => (props.disabled ? '#999' : colorMap[props.variant] || colorMap.default)};
  color: ${props => (props.disabled ? '#fff' : 'inherit')};

  &:hover {
    background-color: ${props =>
    props.disabled ? '#999' : hoverColorMap[props.variant] || hoverColorMap.default};
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
  padding-right: 8px;
`;

const ButtonText = styled.label`
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;
